export const TermsOfServiceScreen = () => {
  return <TermsOfServiceText fullPage />
}

export const TermsOfServiceText = ({
  fullPage = false,
}: {
  fullPage?: boolean
}) => {
  let headerClassName = "text-lg font-bold"
  let updatedAtClassName = "text-sm text-gray-999 pt-0.5 font-normal"
  let containerClassName = "flex flex-col gap-4"

  if (fullPage) {
    headerClassName = "text-2xl font-bold"
    updatedAtClassName = "text-base text-gray-999 pt-1 font-normal"
    containerClassName = "max-w-screen-md mx-auto py-16 flex flex-col gap-4"
  }

  return (
    <div className={containerClassName}>
      <h1 className={headerClassName}>
        Candidate Terms of Use
        <div className={updatedAtClassName}>Effective date: April 21, 2025</div>
      </h1>
      <p>
        Welcome to MeritFirst. Please read on to learn the rules and
        restrictions that govern your use of our website(s), products, services
        and applications (the “Services”). Please note that your access to the
        Services may be subject to a separate agreement between MeritFirst and
        the hiring company (“Hiring Company”) who may have directed you to use
        the Services as part of your application. If you have any questions
        related to the role for which you are applying, please contact the
        Hiring Company. If you have any questions, comments, or concerns
        regarding these terms or the Services, please contact us at:
      </p>
      <p>Email: team@meritfirst.us</p>
      <p>
        These Terms of Use (the “Terms”) are a binding contract between you and{" "}
        <strong>MERIT FIRST, INC.</strong> (“MeritFirst,” “we” and “us”). Your
        use of the Services in any way means that you agree to all of these
        Terms, and these Terms will remain in effect while you use the Services.
        These Terms include the provisions in this document as well as those in
        the Privacy Policy.{" "}
        <strong>
          Your use of or participation in certain Services may also be subject
          to additional policies, rules and/or conditions (“Additional Terms”),
          which are incorporated herein by reference, and you understand and
          agree that by using or participating in any such Services, you agree
          to also comply with these Additional Terms.
        </strong>
      </p>
      <p>
        <strong>Please read these Terms carefully.</strong> They cover important
        information about Services provided to you and any charges, taxes, and
        fees we bill you.{" "}
        <strong>
          These Terms include information about future changes to these Terms,
          limitations of liability, a class action waiver and resolution of
          disputes by arbitration instead of in court. PLEASE NOTE THAT YOUR USE
          OF AND ACCESS TO OUR SERVICES ARE SUBJECT TO THE FOLLOWING TERMS; IF
          YOU DO NOT AGREE TO ALL OF THE FOLLOWING, YOU MAY NOT USE OR ACCESS
          THE SERVICES IN ANY MANNER.
        </strong>
      </p>
      <p>
        <strong>ARBITRATION NOTICE AND CLASS ACTION WAIVER:</strong> EXCEPT FOR
        CERTAIN TYPES OF DISPUTES DESCRIBED IN THE ARBITRATION AGREEMENT SECTION
        BELOW, YOU AGREE THAT DISPUTES BETWEEN YOU AND US WILL BE RESOLVED BY
        BINDING, INDIVIDUAL ARBITRATION AND YOU WAIVE YOUR RIGHT TO PARTICIPATE
        IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
      </p>

      <h2 className="font-bold underline">Will these Terms ever change?</h2>
      <p>
        We are constantly trying to improve our Services, so these Terms may
        need to change along with our Services. We reserve the right to change
        the Terms at any time, but if we do, we will place a notice on our site
        located at standardaptitude.com, send you an email, and/or notify you by
        some other means.{" "}
      </p>
      <p>
        If you don't agree with the new Terms, you are free to reject them;
        unfortunately, that means you will no longer be able to use the
        Services. If you use the Services in any way after a change to the Terms
        is effective, that means you agree to all of the changes.
      </p>
      <p>
        Except for changes by us as described here, no other amendment or
        modification of these Terms will be effective unless in writing and
        signed by both you and us.
      </p>
      <h2 className="font-bold underline">What about my privacy?</h2>
      <p>
        MeritFirst takes the privacy of its users very seriously. For the
        current MeritFirst Privacy Policy, please{" "}
        <a
          className="text-blue-600 underline"
          href="https://app.meritfirst.us/privacy-policy"
        >
          click here
        </a>
        . Any references to “Personal Information” in these Terms will have the
        meaning set forth in the Privacy Policy.
      </p>
      <h2 className="italic underline">
        Children's Online Privacy Protection Act
      </h2>
      <p>
        The Children's Online Privacy Protection Act (“COPPA”) requires that
        online service providers obtain parental consent before they knowingly
        collect personally identifiable information online from children who are
        under 16 years of age. We do not knowingly collect or solicit personally
        identifiable information from children under 16 years of age; if you are
        a child under 16 years of age, please do not attempt to register for or
        otherwise use the Services or send us any personal information. If we
        learn we have collected personal information from a child under 16 years
        of age, we will delete that information as quickly as possible. If you
        believe that a child under 16 years of age may have provided us personal
        information, please contact us at team@meritfirst.us.
      </p>
      <h2 className="font-bold underline">
        What are the basics of using MeritFirst?
      </h2>
      <p>
        You may be required to sign up for an account, select a password and
        user name (“MeritFirst User ID”), and provide us with certain
        information or data, such as your contact information. You promise to
        provide us with accurate, complete, and updated registration information
        about yourself. You may not select as your MeritFirst User ID a name
        that you do not have the right to use, or another person's name with the
        intent to impersonate that person. You may not transfer your account to
        anyone else without our prior written permission.
      </p>
      <p>
        You represent and warrant that you are an individual of legal age to
        form a binding contract (or if not, you've received your parent's or
        guardian's permission to use the Services and have gotten your parent or
        guardian to agree to these Terms on your behalf). If you're agreeing to
        these Terms on behalf of an organization or entity, you represent and
        warrant that you are authorized to agree to these Terms on that
        organization's or entity's behalf and bind them to these Terms (in which
        case, the references to “you” and “your” in these Terms, except for in
        this sentence, refer to that organization or entity).
      </p>
      <p>
        You will only use the Services for your own internal, personal,
        non-commercial use, and not on behalf of or for the benefit of any third
        party, and only in a manner that complies with all laws that apply to
        you. If your use of the Services is prohibited by applicable laws, then
        you aren't authorized to use the Services. We can't and won't be
        responsible for your using the Services in a way that breaks the law.
      </p>
      <p>
        You will not share your MeritFirst User ID, account or password with
        anyone, and you must protect the security of your MeritFirst User ID,
        account, password and any other access tools or credentials. You're
        responsible for any activity associated with your MeritFirst User ID and
        account.
      </p>
      <p>
        You also agree that you will not share any data or information that you
        create while using the Service with any other party. Any task, statement
        or information you access on the Services (including exam materials
        provided to you) is MeritFirst's confidential information and must not
        be shared with any other party.
      </p>
      <h2 className="font-bold underline">What about messaging?</h2>
      <p>
        As part of the Services, you may receive communications through the
        Services, including messages that MeritFirst sends you (for example, via
        email or SMS). When signing up for the Services, you will receive a
        welcome message and instructions on how to stop receiving messages.{" "}
        <strong>
          By signing up for the Services and providing us with your wireless
          number, you confirm that you want MeritFirst to send you information
          that we think may be of interest to you, which may include MeritFirst
          using automated dialing technology to text you at the wireless number
          you provided, and you agree to receive communications from MeritFirst,
          and you represent and warrant that each person you register for the
          Services or for whom you provide a wireless phone number has consented
          to receive communications from MeritFirst.
        </strong>{" "}
        You agree to indemnify and hold MeritFirst harmless from and against any
        and all claims, liabilities, damages (actual and consequential), losses
        and expenses (including attorneys' fees) arising from or in any way
        related to your breach of the foregoing.
      </p>
      <h2 className="font-bold underline">
        Are there restrictions in how I can use the Services?
      </h2>
      <p>
        You represent, warrant, and agree that you will not provide or
        contribute anything, including any Content or User Submission (as those
        terms are defined below), to the Services, or otherwise use or interact
        with the Services, in a manner that:
      </p>
      <ol className="list-decimal list-outside ml-10">
        <li>
          infringes or violates the intellectual property rights or any other
          rights of anyone else (including MeritFirst);
        </li>
        <li>
          violates any law or regulation, including, without limitation, any
          applicable export control laws, privacy laws or any other purpose not
          reasonably intended by MeritFirst;
        </li>
        <li>
          is dangerous, harmful, fraudulent, deceptive, threatening, harassing,
          defamatory, obscene, or otherwise objectionable;
        </li>
        <li>
          jeopardizes the security of your MeritFirst User ID, account or anyone
          else's (such as allowing someone else to log in to the Services as
          you);
        </li>
        <li>
          attempts, in any manner, to obtain the password, account, or other
          security information from any other user;
        </li>
        <li>
          violates the security of any computer network, or cracks any passwords
          or security encryption codes;
        </li>
        <li>
          runs Maillist, Listserv, any form of auto-responder or “spam” on the
          Services, or any processes that run or are activated while you are not
          logged into the Services, or that otherwise interfere with the proper
          working of the Services (including by placing an unreasonable load on
          the Services' infrastructure);
        </li>
        <li>
          “crawls,” “scrapes,” or “spiders” any page, data, or portion of or
          relating to the Services or Content (through use of manual or
          automated means);
        </li>
        <li>copies or stores any significant portion of the Content; or</li>
        <li>
          decompiles, reverse engineers, or otherwise attempts to obtain the
          source code or underlying ideas or information of or relating to the
          Services.
        </li>
      </ol>
      <p>
        A violation of any of the foregoing is grounds for termination of your
        right to use or access the Services.
      </p>
      <h2 className="italic underline">Exam Conduct</h2>
      <p>
        When participating in an assessment, test, quiz, or interview (“Exam”),
        you agree to treat all related content, including questions, materials,
        prompts, and discussions (collectively, “Exam Materials”), as strictly
        confidential. You may not download, copy, capture, share, or otherwise
        extract any Exam Materials, unless explicitly permitted in writing by
        MeritFirst or instructed otherwise in official Exam guidelines.
      </p>
      <p>
        Your Exam responses must be created entirely by you, without assistance
        from others or the use of unauthorized tools. Use of external resources
        is permitted only if expressly allowed by the interviewer or Exam
        instructions. You also agree not to incorporate or disclose any
        proprietary, trade secret, or otherwise confidential information
        belonging to a third party, including any information from past or
        current employers or clients. If you recognize or have encountered any
        portion of the Exam Materials before your session, you are required to
        notify MeritFirst immediately.
      </p>
      <h2 className="font-bold underline">
        What are my rights in the Services?
      </h2>
      <p>
        The materials displayed or performed or available on or through the
        Services, including, but not limited to, text, graphics, data, articles,
        photos, images, illustrations, User Submissions (as defined below) and
        so forth (all of the foregoing, the “Content”) are protected by
        copyright and/or other intellectual property laws. You promise to abide
        by all copyright notices, trademark rules, information, and restrictions
        contained in any Content you access through the Services, and you won't
        use, copy, reproduce, modify, translate, publish, broadcast, transmit,
        distribute, perform, upload, display, license, sell, commercialize or
        otherwise exploit for any purpose any Content not owned by you, (i)
        without the prior consent of the owner of that Content or (ii) in a way
        that violates someone else's (including MeritFirst's) rights.
      </p>
      <p>
        Subject to these Terms, we grant each user of the Services a worldwide,
        non-exclusive, non-sublicensable and non-transferable license to use
        (i.e., to download and display locally) Content solely for purposes of
        using the Services. Use, reproduction, modification, distribution or
        storage of any Content for any purpose other than using the Services is
        expressly prohibited without prior written permission from us. You
        understand that MeritFirst owns the Services. You won't modify, publish,
        transmit, participate in the transfer or sale of, reproduce (except as
        expressly provided in this Section), create derivative works based on,
        or otherwise exploit any of the Services. The Services may allow you to
        copy or download certain Content, but please remember that even where
        these functionalities exist, all the restrictions in this section still
        apply.
      </p>
      <h2 className="font-bold underline">
        What about anything I contribute to the Services – do I have to grant
        any licenses to MeritFirst or to other users?
      </h2>
      <h3 className="italic underline">User Submissions</h3>
      <p>
        Anything you post, upload, share, store, or otherwise provide through
        the Services is your “User Submission”. Some User Submissions may be
        viewable by other users. You are solely responsible for all User
        Submissions you contribute to the Services. You represent that all User
        Submissions submitted by you are accurate, complete, up-to-date, and in
        compliance with all applicable laws, rules and regulations.
      </p>
      <p>
        You agree that you will not post, upload, share, store, or otherwise
        provide through the Services any User Submissions that: (i) infringe any
        third party's copyrights or other rights (e.g., trademark, privacy
        rights, etc.); (ii) contain sexually explicit content or pornography;
        (iii) contain hateful, defamatory, or discriminatory content or incite
        hatred against any individual or group; (iv) exploit minors; (v) depict
        unlawful acts or extreme violence; (vi) depict animal cruelty or extreme
        violence towards animals; (vii) promote fraudulent schemes, multi-level
        marketing (MLM) schemes, get rich quick schemes, online gaming and
        gambling, cash gifting, work from home businesses, or any other dubious
        money-making ventures; or (viii) that violate any law.
      </p>
      <h3 className="italic underline">Exam Results</h3>
      <p>
        By participating in an Exam, you consent to MeritFirst recording your
        responses, audio, video, and any activity in connection with the
        Services. You also authorize MeritFirst to share recordings and
        information related to your Exam performance, including your responses,
        scores, and other outcome data (“Exam Results”), in accordance with its{" "}
        <a
          href="https://app.meritfirst.us/privacy-policy"
          className="text-blue-600 underline"
        >
          Privacy Policy
        </a>
        . MeritFirst shall own all rights, title, and interest in the Exam, the
        Exam Materials, and the Exam Results (collectively, the “Assigned
        Content”), and any derivative works arising from them, including all
        associated intellectual property rights. You hereby make and agree to
        make all assignments to MeritFirst necessary to effectuate and
        accomplish MeritFirst's ownership in and to the Assigned Content.{" "}
      </p>
      <h3 className="italic underline">Licenses</h3>
      <p>
        In order to display your User Submissions on the Services, and to allow
        other users to enjoy them (where applicable), you grant us certain
        rights in those User Submissions (see below for more information).
        Please note that all of the following licenses are subject to our{" "}
        <a
          href="https://app.meritfirst.us/privacy-policy"
          className="text-blue-600 underline"
        >
          Privacy Policy
        </a>{" "}
        to the extent they relate to User Submissions that are also your
        personally-identifiable information.
      </p>
      <p>
        By submitting User Submissions through the Services, you hereby do and
        shall grant MeritFirst a worldwide, non-exclusive, perpetual,
        royalty-free, fully paid, sublicensable and transferable license to use,
        edit, modify, truncate, aggregate, reproduce, distribute, prepare
        derivative works of, display, perform, and otherwise fully exploit the
        User Submissions in connection with this site, the Services and our (and
        our successors' and assigns') businesses, including without limitation
        for promoting and redistributing part or all of this site or the
        Services (and derivative works thereof) in any media formats and through
        any media channels (including, without limitation, third party websites
        and feeds), and including after your termination of your account or the
        Services. You also hereby do and shall grant each user of this site
        and/or the Services a non-exclusive, perpetual license to access your
        User Submissions through this site and/or the Services, and to use,
        edit, modify, reproduce, distribute, prepare derivative works of,
        display and perform such User Submissions, including after your
        termination of your account or the Services. For clarity, the foregoing
        license grants to us and our users do not affect your other ownership or
        license rights in your User Submissions, including the right to grant
        additional licenses to your User Submissions, unless otherwise agreed in
        writing. You represent and warrant that you have all rights to grant
        such licenses to us without infringement or violation of any third party
        rights, including without limitation, any privacy rights, publicity
        rights, copyrights, trademarks, contract rights, or any other
        intellectual property or proprietary rights.
      </p>
      <p>
        Certain features of the Services allow you to share information with
        others, including through your social networks or other Third Party
        Accounts. When Content is authorized for sharing, we will clearly
        identify the Content you are authorized to redistribute and the ways you
        may redistribute it, usually by providing a “share” button on or near
        the Content. If you share information from the Services with others
        through your Third Party Accounts, such as your social networks, you
        authorize MeritFirst to share that information with the applicable Third
        Party Account provider. Please review the policies of any Third Party
        Account providers you share information with or through for additional
        information about how they may use your information. If you redistribute
        Content, you must be able to edit or delete any Content you
        redistribute, and you must edit or delete it promptly upon our request.
      </p>
      <p>
        Finally, you understand and agree that MeritFirst, in performing the
        required technical steps to provide the Services to our users (including
        you), may need to make changes to your User Submissions to conform and
        adapt those User Submissions to the technical requirements of connection
        networks, devices, services, or media, and the foregoing licenses
        include the rights to do so.
      </p>
      <h2 className="font-bold underline">
        Who is responsible for what I see and do on the Services?
      </h2>
      <p>
        Any information or Content transmitted through the Services is the sole
        responsibility of the person from whom such Content originated, and you
        access all such information and Content at your own risk, and we aren't
        liable for any errors or omissions in that information or Content or for
        any damages or loss you might suffer in connection with it. We cannot
        control and have no duty to take any action regarding how you may
        interpret and use the Content or what actions you may take as a result
        of having been exposed to the Content, and you hereby release us from
        all liability for you having acquired or not acquired Content through
        the Services. We can't guarantee the identity of any users with whom you
        interact in using the Services and are not responsible for which users
        gain access to the Services.
      </p>
      <p>
        You are responsible for all Content you contribute, in any manner, to
        the Services, and you represent and warrant you have all rights
        necessary to do so, in the manner in which you contribute it.
      </p>
      <p>
        The Services may contain links or connections to third-party websites or
        services that are not owned or controlled by MeritFirst. When you access
        third-party websites or use third-party services, you accept that there
        are risks in doing so, and that MeritFirst is not responsible for such
        risks.
      </p>
      <p>
        MeritFirst has no control over, and assumes no responsibility for, the
        content, accuracy, privacy policies, or practices of or opinions
        expressed in any third-party websites or by any third party that you
        interact with through the Services. In addition, MeritFirst will not and
        cannot monitor, verify, censor or edit the content of any third-party
        site or service. We encourage you to be aware when you leave the
        Services and to read the terms and conditions and privacy policy of each
        third-party website or service that you visit or utilize. By using the
        Services, you release and hold us harmless from any and all liability
        arising from your use of any third-party website or service.
      </p>
      <p>
        Your interactions with organizations and/or individuals found on or
        through the Services, including payment and delivery of goods or
        services, and any other terms, conditions, warranties or representations
        associated with such dealings, are solely between you and such
        organizations and/or individuals. You should make whatever investigation
        you feel necessary or appropriate before proceeding with any online or
        offline transaction with any of these third parties. You agree that
        MeritFirst shall not be responsible or liable for any loss or damage of
        any sort incurred as the result of any such dealings.
      </p>
      <p>
        If there is a dispute between participants on this site or Services, or
        between users and any third party, you agree that MeritFirst is under no
        obligation to become involved. In the event that you have a dispute with
        one or more other users, you release MeritFirst, its directors,
        officers, employees, agents, and successors from claims, demands, and
        damages of every kind or nature, known or unknown, suspected or
        unsuspected, disclosed or undisclosed, arising out of or in any way
        related to such disputes and/or our Services. You shall and hereby do
        waive California Civil Code Section 1542 or any similar law of any
        jurisdiction, which says in substance: “A general release does not
        extend to claims that the creditor or releasing party does not know or
        suspect to exist in his or her favor at the time of executing the
        release and that, if known by him or her, would have materially affected
        his or her settlement with the debtor or released party.”
      </p>
      <h2 className="font-bold underline">
        Will MeritFirst ever change the Services?
      </h2>
      <p>
        We're always trying to improve our Services, so they may change over
        time. We may suspend or discontinue any part of the Services, or we may
        introduce new features or impose limits on certain features or restrict
        access to parts or all of the Services. We'll try to give you notice
        when we make a material change to the Services that would adversely
        affect you, but this isn't always practical. We reserve the right to
        remove any Content from the Services at any time, for any reason
        (including, but not limited to, if someone alleges you contributed that
        Content in violation of these Terms), in our sole discretion, and
        without notice.
      </p>
      <h2 className="font-bold underline">
        What if I want to stop using the Services?
      </h2>
      <p>
        You're free to do that at any time by contacting us at
        team@meritfirst.us; please refer to our{" "}
        <a
          href="https://app.meritfirst.us/privacy-policy"
          className="text-blue-600 underline"
        >
          Privacy Policy
        </a>
        , as well as the licenses above, to understand how we treat information
        you provide to us after you have stopped using our Services.
      </p>
      <p>
        MeritFirst is also free to terminate (or suspend access to) your use of
        the Services or your account for any reason in our discretion, including
        your breach of these Terms. MeritFirst has the sole right to decide
        whether you are in violation of any of the restrictions set forth in
        these Terms.
      </p>
      <p>
        Account termination may result in destruction of any Content associated
        with your account, so keep that in mind before you decide to terminate
        your account.
      </p>
      <p>
        If you have deleted your account by mistake, contact us immediately at
        team@meritfirst.us – we will try to help, but unfortunately, we can't
        promise that we can recover or restore anything.
      </p>
      <p>
        Provisions that, by their nature, should survive termination of these
        Terms shall survive termination. By way of example, all of the following
        will survive termination: any obligation you have to pay us or indemnify
        us, any limitations on our liability, any terms regarding ownership or
        intellectual property.
      </p>
      <h2 className="font-bold underline">What else do I need to know?</h2>
      <p>
        <em className="underline">Warranty Disclaimer.</em> MeritFirst and its
        licensors, suppliers, partners, parent, subsidiaries or affiliated
        entities, and each of their respective officers, directors, members,
        employees, consultants, contract employees, representatives and agents,
        and each of their respective successors and assigns (MeritFirst and all
        such parties together, the “MeritFirst Parties”) make no representations
        or warranties concerning the Services, including without limitation
        regarding any Content contained in or accessed through the Services, and
        the MeritFirst Parties will not be responsible or liable for the
        accuracy, copyright compliance, legality, or decency of material
        contained in or accessed through the Services or any claims, actions,
        suits procedures, costs, expenses, damages or liabilities arising out of
        use of, or in any way related to your participation in, the Services.
        The MeritFirst Parties make no representations or warranties regarding
        suggestions or recommendations of services or products offered or
        purchased through or in connection with the Services. THE SERVICES AND
        CONTENT ARE PROVIDED BY MERITFIRST (AND ITS LICENSORS AND SUPPLIERS) ON
        AN “AS-IS” BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR
        IMPLIED, INCLUDING, WITHOUT LIMITATION, IMPLIED WARRANTIES OF
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR
        THAT USE OF THE SERVICES WILL BE UNINTERRUPTED OR ERROR-FREE. SOME
        STATES DO NOT ALLOW LIMITATIONS ON HOW LONG AN IMPLIED WARRANTY LASTS,
        SO THE ABOVE LIMITATIONS MAY NOT APPLY TO YOU.
      </p>
      <p>
        <em className="underline">Limitation of Liability.</em> TO THE FULLEST
        EXTENT ALLOWED BY APPLICABLE LAW, UNDER NO CIRCUMSTANCES AND UNDER NO
        LEGAL THEORY (INCLUDING, WITHOUT LIMITATION, TORT, CONTRACT, STRICT
        LIABILITY, OR OTHERWISE) SHALL ANY OF THE MERITFIRST PARTIES BE LIABLE
        TO YOU OR TO ANY OTHER PERSON FOR (A) ANY INDIRECT, SPECIAL, INCIDENTAL,
        PUNITIVE OR CONSEQUENTIAL DAMAGES OF ANY KIND, INCLUDING DAMAGES FOR
        LOST PROFITS, BUSINESS INTERRUPTION, LOSS OF DATA, LOSS OF GOODWILL,
        WORK STOPPAGE, ACCURACY OF RESULTS, OR COMPUTER FAILURE OR MALFUNCTION,
        (B) ANY SUBSTITUTE GOODS, SERVICES OR TECHNOLOGY, (C) ANY AMOUNT, IN THE
        AGGREGATE, IN EXCESS OF THE GREATER OF (I) ONE-HUNDRED ($100) DOLLARS OR
        (II) THE AMOUNTS PAID AND/OR PAYABLE BY YOU TO MERITFIRST IN CONNECTION
        WITH THE SERVICES IN THE TWELVE (12) MONTH PERIOD PRECEDING THIS
        APPLICABLE CLAIM OR (D) ANY MATTER BEYOND OUR REASONABLE CONTROL. SOME
        STATES DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL OR
        CONSEQUENTIAL OR CERTAIN OTHER DAMAGES, SO THE ABOVE LIMITATION AND
        EXCLUSIONS MAY NOT APPLY TO YOU.
      </p>
      <p>
        <em className="underline">Indemnity.</em> You agree to indemnify and
        hold the MeritFirst Parties harmless from and against any and all
        claims, liabilities, damages (actual and consequential), losses and
        expenses (including attorneys' fees) arising from or in any way related
        to any claims relating to (a) your use of the Services (including any
        actions taken by a third party using your account), and (b) your
        violation of these Terms. In the event of such a claim, suit, or action
        (“Claim”), we will attempt to provide notice of the Claim to the contact
        information we have for your account (provided that failure to deliver
        such notice shall not eliminate or reduce your indemnification
        obligations hereunder).
      </p>
      <p>
        <em className="underline">Assignment.</em> You may not assign, delegate
        or transfer these Terms or your rights or obligations hereunder, or your
        Services account, in any way (by operation of law or otherwise) without
        MeritFirst's prior written consent. We may transfer, assign, or delegate
        these Terms and our rights and obligations without consent.
      </p>
      <p>
        <em className="underline">Choice of Law.</em> These Terms are governed
        by and will be construed under the Federal Arbitration Act, applicable
        federal law, and the laws of the State of Texas, without regard to the
        conflicts of laws provisions thereof.
      </p>
      <p>
        <em className="underline">Arbitration Agreement.</em> Please read the
        following ARBITRATION AGREEMENT carefully because it requires you to
        arbitrate certain disputes and claims with MeritFirst and limits the
        manner in which you can seek relief from MeritFirst. Both you and
        MeritFirst acknowledge and agree that for the purposes of any dispute
        arising out of or relating to the subject matter of these Terms,
        MeritFirst's officers, directors, employees and independent contractors
        (“Personnel”) are third-party beneficiaries of these Terms, and that
        upon your acceptance of these Terms, Personnel will have the right (and
        will be deemed to have accepted the right) to enforce these Terms
        against you as the third-party beneficiary hereof.
      </p>
      <p>
        (a) <em>Arbitration Rules; Applicability of Arbitration Agreement.</em>{" "}
        The parties shall use their best efforts to settle any dispute, claim,
        question, or disagreement arising out of or relating to the subject
        matter of these Terms directly through good-faith negotiations, which
        shall be a precondition to either party initiating arbitration. If such
        negotiations do not resolve the dispute, it shall be finally settled by
        binding arbitration in Travis County, Texas. The arbitration will
        proceed in the English language, in accordance with the JAMS Streamlined
        Arbitration Rules and Procedures (the “Rules”) then in effect, by one
        commercial arbitrator with substantial experience in resolving
        intellectual property and commercial contract disputes. The arbitrator
        shall be selected from the appropriate list of JAMS arbitrators in
        accordance with such Rules. Judgment upon the award rendered by such
        arbitrator may be entered in any court of competent jurisdiction.
      </p>
      <p>
        (b) <em>Costs of Arbitration.</em> The Rules will govern payment of all
        arbitration fees. MeritFirst will pay all arbitration fees for claims
        less than seventy-five thousand ($75,000) dollars. MeritFirst will not
        seek its attorneys' fees and costs in arbitration unless the arbitrator
        determines that your claim is frivolous.{" "}
      </p>
      <p>
        (c) <em>Small Claims Court; Infringement.</em> Either you or MeritFirst
        may assert claims, if they qualify, in small claims court in Travis
        County, Texas or any United States county where you live or work.
        Furthermore, notwithstanding the foregoing obligation to arbitrate
        disputes, each party shall have the right to pursue injunctive or other
        equitable relief at any time, from any court of competent jurisdiction,
        to prevent the actual or threatened infringement, misappropriation or
        violation of a party's copyrights, trademarks, trade secrets, patents or
        other intellectual property rights.
      </p>
      <p>
        (d) <em>Waiver of Jury Trial.</em> YOU AND MERITFIRST WAIVE ANY
        CONSTITUTIONAL AND STATUTORY RIGHTS TO GO TO COURT AND HAVE A TRIAL IN
        FRONT OF A JUDGE OR JURY. You and MeritFirst are instead choosing to
        have claims and disputes resolved by arbitration. Arbitration procedures
        are typically more limited, more efficient, and less costly than rules
        applicable in court and are subject to very limited review by a court.
        In any litigation between you and MeritFirst over whether to vacate or
        enforce an arbitration award, YOU AND MERITFIRST WAIVE ALL RIGHTS TO A
        JURY TRIAL, and elect instead to have the dispute be resolved by a
        judge.
      </p>
      <p>
        (e) <em>Waiver of Class or Consolidated Actions.</em> ALL CLAIMS AND
        DISPUTES WITHIN THE SCOPE OF THIS ARBITRATION AGREEMENT MUST BE
        ARBITRATED OR LITIGATED ON AN INDIVIDUAL BASIS AND NOT ON A CLASS BASIS.
        CLAIMS OF MORE THAN ONE CUSTOMER OR USER CANNOT BE ARBITRATED OR
        LITIGATED JOINTLY OR CONSOLIDATED WITH THOSE OF ANY OTHER CUSTOMER OR
        USER. If however, this waiver of class or consolidated actions is deemed
        invalid or unenforceable, neither you nor MeritFirst is entitled to
        arbitration; instead all claims and disputes will be resolved in a court
        as set forth in (g) below.
      </p>
      <p>
        (f) <em>Opt-out.</em> You have the right to opt out of the provisions of
        this Section by sending written notice of your decision to opt out to
        the following address: 907 S Congress Ave., Austin, Texas 78704
        postmarked within thirty (30) days of first accepting these Terms. You
        must include (i) your name and residence address, (ii) the email address
        and/or telephone number associated with your account, and (iii) a clear
        statement that you want to opt out of these Terms' arbitration
        agreement.
      </p>
      <p>
        (g) <em>Exclusive Venue.</em> If you send the opt-out notice in (f),
        and/or in any circumstances where the foregoing arbitration agreement
        permits either you or MeritFirst to litigate any dispute arising out of
        or relating to the subject matter of these Terms in court, then the
        foregoing arbitration agreement will not apply to either party, and both
        you and MeritFirst agree that any judicial proceeding (other than small
        claims actions) will be brought in the state or federal courts located
        in, respectively, Travis County, Texas, or the federal district in which
        that county falls.
      </p>
      <p>
        (h) <em>Severability.</em> If the prohibition against class actions and
        other claims brought on behalf of third parties contained above is found
        to be unenforceable, then all of the preceding language in this
        Arbitration Agreement section will be null and void. This arbitration
        agreement will survive the termination of your relationship with
        MeritFirst.
      </p>
      <p>
        <em className="underline">Miscellaneous.</em> You will be responsible
        for paying, withholding, filing, and reporting all taxes, duties, and
        other governmental assessments associated with your activity in
        connection with the Services, provided that the MeritFirst may, in its
        sole discretion, do any of the foregoing on your behalf or for itself as
        it sees fit. The failure of either you or us to exercise, in any way,
        any right herein shall not be deemed a waiver of any further rights
        hereunder. If any provision of these Terms are found to be unenforceable
        or invalid, that provision will be limited or eliminated, to the minimum
        extent necessary, so that these Terms shall otherwise remain in full
        force and effect and enforceable. You and MeritFirst agree that these
        Terms are the complete and exclusive statement of the mutual
        understanding between you and MeritFirst, and that these Terms supersede
        and cancel all previous written and oral agreements, communications and
        other understandings relating to the subject matter of these Terms. You
        hereby acknowledge and agree that you are not an employee, agent,
        partner, or joint venture of MeritFirst, and you do not have any
        authority of any kind to bind MeritFirst in any respect whatsoever.
      </p>
      <p>
        Except as expressly set forth in the section above regarding the
        arbitration agreement, you and MeritFirst agree there are no third-party
        beneficiaries intended under these Terms.
      </p>

      <hr className="my-10" />
      <h1 className={headerClassName}>
        Company Terms and Conditions
        <div className={updatedAtClassName}>Effective date: April 21, 2025</div>
      </h1>
      <p className="font-bold">
        PLEASE READ THESE TERMS AND CONDITIONS CAREFULLY BEFORE USING THE
        SERVICES OFFERED BY MERIT FIRST, INC. (“MERITFIRST”). BY SIGNING AN
        ORDER FORM, STATEMENT OF WORK, AGREEING TO AN ONLINE SUBSCRIPTION THAT
        REFERENCES THESE TERMS AND CONDITIONS, OR USING THE SERVICES (EACH AN
        “ORDER”), YOU OR THE ENTITY THAT YOU REPRESENT (“CUSTOMER”) ARE
        UNCONDITIONALLY CONSENTING TO BE BOUND BY AND ARE BECOMING A PARTY TO
        THIS AGREEMENT CONSISTING OF THIS PARAGRAPH AND THE FOLLOWING TERMS AND
        CONDITIONS (COLLECTIVELY THE “AGREEMENT”). PROVISION OF THE SERVICE IS
        CONDITIONED ON, AND CUSTOMER'S ACCESS TO OR USE OF THE SERVICE SHALL
        CONSTITUTE, CUSTOMER'S ASSENT TO THE TERMS OF THIS AGREEMENT TO THE
        EXCLUSION OF ALL OTHER TERMS. IF THESE TERMS ARE CONSIDERED AN OFFER,
        ACCEPTANCE IS EXPRESSLY LIMITED TO SUCH TERMS. IN THE EVENT OF A
        CONFLICT BETWEEN THIS AGREEMENT AND THE ORDER FORM, THE ORDER FORM SHALL
        CONTROL.
      </p>
      <div className="flex flex-col gap-4">
        <div className="flex items-start">
          <p className="w-[30px] font-medium">
            <strong>1.</strong>
          </p>
          <p className="flex-1">
            <strong>Purpose.</strong>The purpose of this Agreement is to grant
            Customer with access to the Services (defined below) for use in
            Customer's job candidate examination process.
          </p>
        </div>
        <div className="flex items-start">
          <p className="w-[30px] font-medium">
            <strong>2.</strong>
          </p>
          <p className="flex-1">
            <strong>Services.</strong>
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">2.1</p>
          <p className="flex-1">
            <span className="underline">Services Performance.</span> Subject to
            the terms and conditions of this Agreement and the applicable Order
            and Customer's compliance therewith, MeritFirst will perform the
            services set forth in each Order, which may include access to
            certain platforms or products specified therein (collectively,
            “Services”) during the applicable Order Term (as defined below).
            Customer will identify the candidates that it wishes MeritFirst to
            assess (each, a “Candidate”) using MeritFirst's Services and
            examination tools (the “Exam”). Customer will also perform any
            obligations of Customer that may be set forth in the Order.
            MeritFirst will provide Customer with access to certain materials
            prepared by MeritFirst under this Agreement, including those
            relating to Exams (e.g., interview notes, Candidate assessments,
            Candidate performance recommendations, site content, selection
            criteria, technical assessments, derived data, data analytics,
            instructional videos, instructional materials, interview
            methodologies, and other similar materials) (“Exam Materials”) and
            certain other materials provided to Customer under this Agreement
            (together with the Exam Materials, the “Licensed Materials”), which
            shall be considered a part of the Services and governed by the terms
            of this Agreement. MeritFirst will provide Customer with a final
            report for each Candidate summarizing each Exam, as described in the
            Order (the “Exam Results”). Each Customer employee who accesses the
            Services must establish their own account for such access. Customer
            is solely responsible for all of Customer's activity in connection
            with the Services, including but not limited to uploading Customer
            Data (as defined below) onto the Services.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">2.2</p>
          <p className="flex-1">
            <span className="underline">Ownership.</span> As between the
            parties, MeritFirst retains all right, title, and interest in and to
            the Services, Licensed Materials, and all software, products, works,
            and other intellectual property and moral rights related thereto or
            created, used, or provided by MeritFirst for the purposes of this
            Agreement, including any copies and derivative works of the
            foregoing. Any software that is distributed or otherwise provided to
            Customer hereunder (including without limitation any software
            identified on an Order) shall be deemed a part of the Services and
            subject to all of the terms and conditions of this Agreement. No
            rights or licenses are granted except as expressly and unambiguously
            set forth in this Agreement.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">2.3</p>
          <p className="flex-1">
            <span className="underline">Restrictions.</span> Except as expressly
            set forth in this Agreement, Customer shall not (and shall not
            permit any third party to), directly or indirectly: (a) reverse
            engineer, decompile, disassemble, or otherwise attempt to discover
            the source code, object code, or underlying structure, ideas, or
            algorithms of the Services (except to the extent applicable laws
            specifically prohibit such restriction); (b) modify, translate, or
            create derivative works based on the Services; (c) copy, rent,
            lease, distribute, pledge, assign, or otherwise transfer or encumber
            rights to the Services; (d) use the Services for the benefit of a
            third party; (e) remove or otherwise alter any proprietary notices
            or labels from the Services or any portion thereof; (vi) use the
            Services to build an application or product that is competitive with
            any MeritFirst product or service; (vii) interfere or attempt to
            interfere with the proper working of the Services or any activities
            conducted on the Services; or (viii) bypass any measures MeritFirst
            may use to prevent or restrict access to the Services (or other
            accounts, computer systems or networks connected to the Services).
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">2.4</p>
          <p className="flex-1">
            <span className="underline">Compliance.</span> Customer (i) shall
            use the Services in compliance with all applicable local, state,
            national and foreign laws, treaties and regulations in connection
            with Customer's use of the Services (including those related to data
            privacy, international communications, export laws and the
            transmission of technical or personal data laws), and (ii) shall not
            use the Services in a manner that violates any third party
            intellectual property, contractual or other proprietary rights.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>3.</strong>
          </p>
          <p className="flex-1">
            <strong>Payment Terms.</strong> Customer shall pay MeritFirst the
            fees set forth in the applicable Order, if any (“Fees”). Fees will
            be due Net 30 days after the date of invoice. Past due invoices are
            subject to interest on any outstanding balance of the lesser of 1.5%
            per month or the maximum amount permitted by law. Customer shall be
            responsible for all taxes associated with Services (excluding taxes
            based on MeritFirst's net income). All Fees paid are non-refundable
            and are not subject to set-off.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>4.</strong>
          </p>
          <p className="flex-1">
            <strong>Term and Termination.</strong>
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">4.1</p>
          <p className="flex-1">
            <span className="underline">Term.</span> The term of this Agreement
            (the “Term”) shall commence on the Effective Date, and unless
            earlier terminated as set forth herein, shall continue until ninety
            (90) days following the expiration of all Order Terms. For each
            Order, unless otherwise specified therein, the “Order Term” shall
            begin as of the effective date set forth on such Order, and unless
            earlier terminated as set forth herein or therein, (a) shall
            continue for the initial term specified on such Order (the “Order
            Initial Term”), and (b) following the Order Initial Term, shall
            automatically renew for additional successive periods of one year
            each (each, an “Order Renewal Term”) unless either party notifies
            the other party of such party's intention not to renew no later than
            thirty (30) days prior to the expiration of the Order Initial Term
            or then-current Order Renewal Term, as applicable.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">4.2</p>
          <p className="flex-1">
            <span className="underline">Termination.</span> Either party may
            terminate this Agreement or any Order for cause if the other party
            has breached any of its obligations under this Agreement or such
            Order and has failed to cure such breach within 30-days after
            receiving written notice to do so from the other party. Upon
            termination, Customer shall have no further access to the Services.
            Customer shall remain responsible for and, upon termination, shall
            promptly pay MeritFirst for all Services performed as of the date of
            termination. If MeritFirst terminates for Customer's material
            breach, Customer shall remain responsible for the Fees, if any, due
            for the remainder of the Agreement. If Customer terminates for
            MeritFirst's material breach, Customer shall receive a refund of the
            Fees prorated from the date of breach. Following notice of
            termination, MeritFirst may suspend all Services without liability
            of any kind or, at its sole option, require Customer to pay for such
            Services in advance.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">4.3</p>
          <p className="flex-1">
            <span className="underline">
              Termination of License; Return of Materials.
            </span>{" "}
            Customer's access to the Services and all licenses granted hereunder
            shall terminate upon the earlier of (a) ninety (90) days after
            completion of the Services and (b) expiration or termination of this
            Agreement. Customer shall return all Licensed Materials to
            MeritFirst upon termination of the license; provided that Customer
            may retain the Exam Results after termination or expiration of this
            Agreement. All provisions of this Agreement that by their nature
            should survive termination shall survive termination, including,
            without limitation, accrued payment obligations, ownership
            provisions, warranty disclaimers, indemnity and limitations of
            liability.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>5</strong>
          </p>
          <p className="flex-1">
            <strong>Confidentiality.</strong> During the Term, a party (“
            <strong>Recipient</strong>”) may be exposed to confidential or
            proprietary information of the other party (“
            <strong>Discloser</strong>”), including hiring requirements,
            processes, criteria, policies, priorities, resources, methods,
            plans, customer lists, and other business information that the
            Discloser designates as confidential or the Recipient knows or
            reasonably should know is considered by the Discloser to be
            confidential or proprietary in nature (“
            <strong>Confidential Information</strong>”). The Recipient agrees,
            during and after the Term, not to use any of the Discloser's
            Confidential Information except for the purposes of this Agreement
            and not to disclose such information to any third party or to assert
            an ownership interest in such information. Further the Recipient
            agrees to take all reasonable precautions to prevent unauthorized or
            inadvertent disclosure of such, and to be responsible for any breach
            of these obligations by its officers, directors, or employees. For
            clarification, all Licensed Materials constitute MeritFirst
            Confidential Information. Customer Confidential Information includes
            selection criteria provided to MeritFirst used by Customer to select
            Candidates (“<strong>Customer Criteria</strong>”), including
            Customer's specific hiring requirements, policies, priorities, and
            resources. Confidential Information does not include information
            available to the public without restriction on its use, information
            provided to the Recipient by a third party under no duty to the
            Discloser to maintain it in confidence, information that the
            Recipient is required to disclose in response to a court order or
            subpoena provided that to the extent legally permissible the
            Discloser is provided prior written notice and an opportunity to
            contest, information rightfully in the Recipient's possession prior
            to disclosure by the Discloser, or information developed by the
            Recipient without use of or reference to the Discloser's
            Confidential Information.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>6.</strong>
          </p>
          <p className="flex-1">
            <strong>Data Use.</strong>
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">6.1</p>
          <p className="flex-1">
            <span className="underline">Licensed Materials.</span> Customer
            acknowledges that, except for any Customer Confidential Information
            included in the Licensed Materials, MeritFirst owns all right,
            title, and interest (including all intellectual property rights) in
            all Licensed Materials and MeritFirst Confidential Information,
            including without limitation in all intellectual property in and
            used to create the Exam Results. MeritFirst grants Customer a
            limited, terminable, non-transferrable license to use the Licensed
            Materials during the Term for Customer's own internal use solely for
            the purposes set forth in this Agreement; such license shall
            automatically terminate at ninety (90) days after the termination or
            expiration of this Agreement. Nothing in this Agreement or otherwise
            shall be interpreted or construed to grant Customer any proprietary
            interest in the Licensed Materials.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">6.2</p>
          <p className="flex-1">
            <span className="underline">Customer Data.</span> MeritFirst
            acknowledges that Customer owns all right, title, and interest
            (including all intellectual property rights) in Customer Criteria,
            Customer Confidential Information and Exam Results to the extent
            such Exam Results do not include Licensed Materials (collectively,
            “Customer Data”). Notwithstanding anything to the contrary, Customer
            acknowledges and agrees that MeritFirst may (a) internally use and
            modify (but not disclose) Customer Data for the purposes of (i)
            providing the Services and any support or consultation services to
            Customer and (ii) generating Aggregated Anonymous Data (as defined
            below), and (b) freely use and make available Aggregated Anonymous
            Data, during and after the Term, for MeritFirst's business purposes
            (including without limitation, for purposes of refining its
            evaluation processes, developing and promoting new product
            offerings, and producing white papers and other publications
            relating to MeritFirst's Exam ). “Aggregated Anonymous Data” means
            data submitted to, collected by, or generated by MeritFirst in
            connection with its performance under this Agreement and from Exams
            and Customer's use of the Services, but only in aggregate,
            anonymized form which can in no way be linked specifically to
            Customer or any individual Candidate.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">6.3</p>
          <p className="flex-1">
            <span className="underline">Candidate Data.</span>
            Notwithstanding anything to the contrary, MeritFirst's collections
            and use of information about Candidates (“Candidate Data”) will be
            governed by MeritFirst's Candidate Terms of Use and Privacy Policy
            (available at{" "}
            <a
              className="text-blue-600 underline"
              href="https://app.meritfirst.us/terms"
            >
              app.meritfirst.us/terms
            </a>{" "}
            and{" "}
            <a
              className="text-blue-600 underline"
              href="https://app.meritfirst.us/privacy-policy"
            >
              app.meritfirst.us/privacy-policy
            </a>
            ), which may be updated by MeritFirst from time to time, and will
            not be considered Customer Data or Customer Confidential
            Information. For the purposes of Exhibit B (if applicable)
            MeritFirst shall be deemed the controller of the Candidate Data.
            MeritFirst agrees that it will not disclose to any third party any
            Exam Materials or Exam Results in a manner that personally
            identifies Candidates without the prior consent of Customer and the
            applicable Candidate. Additionally, MeritFirst will not disclose the
            identity of any Candidate and their relationship with Customer to
            any third party without Customer's prior consent, provided that
            nothing shall prohibit MeritFirst from providing its services to a
            third party with respect to a Candidate if such third party
            independently identified such Candidate.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>7.</strong>
          </p>
          <p className="flex-1">
            <strong>Warranty Disclaimer, Limitation of Liability.</strong>
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">7.1</p>
          <p className="flex-1">
            <span className="underline">Warranty Disclaimer.</span> EXCEPT AS
            EXPRESSLY SET FORTH IN THIS AGREEMENT, MERITFIRST MAKES NO
            WARRANTIES OF ANY KIND WITH RESPECT TO THE SERVICES, EXAM, OR
            LICENSED MATERIALS, ALL OF WHICH ARE PROVIDED “AS IS”. EXCEPT AS
            EXPRESSLY SET FORTH IN THIS AGREEMENT, MERITFIRST DOES NOT WARRANT
            THAT ANY OF THE ABOVE OR THE RESULTS OF THE SERVICES ARE ACCURATE OR
            WILL MEET CUSTOMER'S REQUIREMENTS OR WILL COMPLY WITH CUSTOMER'S
            POLICIES OR APPLICABLE LAW. EXCEPT AS EXPRESSLY SET FORTH IN THIS
            AGREEMENT, MERITFIRST HEREBY DISCLAIMS ALL WARRANTIES, WHETHER
            EXPRESS, IMPLIED OR STATUTORY, INCLUDING BUT NOT LIMITED TO,
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            TITLE, NON-INFRINGEMENT, ACCURACY, INTERFERENCE WITH QUIET ENJOYMENT
            AND ANY WARRANTIES ARISING FROM COURSE OF DEALING, USAGE OR TRADE
            PRACTICE.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">7.2</p>
          <p className="flex-1">
            <span className="underline">Limitations of Automated Outputs.</span>{" "}
            The Exam may be considered an Automated Employment Decision Tool
            (“AEDT”) under applicable laws. The Exam Results are intended solely
            to support and inform Customer's broader evaluative processes of
            Candidates and are not intended for Candidates' use or reliance. It
            is not a substitute for human judgment or comprehensive review and
            should not be used as the sole or predominant basis for any
            employment-related decisions. Specifically, it is not appropriate
            to: (a) rely solely on a simplified output (e.g., a score, tag,
            classification, ranking, etc.), without considering any additional
            factors; (b) use a simplified output as one of several criteria,
            where it is weighted more heavily than any other criterion in the
            set; or (c) use a simplified output to overrule conclusions derived
            from other sources, including human evaluation.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">7.3</p>
          <p className="flex-1">
            <span className="underline">Limitation of Liability.</span> NEITHER
            PARTY SHALL BE LIABLE TO THE OTHER PARTY FOR ANY INDIRECT,
            INCIDENTAL, EXEMPLARY, SPECIAL, PUNITIVE OR CONSEQUENTIAL DAMAGES OR
            LOSSES RELATING TO ANY OF THE SERVICES OR LICENSED MATERIALS,
            WHETHER SUCH LIABILITY IS BASED ON CONTRACT, WARRANTY, TORT
            (INCLUDING NEGLIGENCE), PRODUCT LIABILITY OR OTHERWISE, EVEN IF SUCH
            DAMAGES WERE FORESEEABLE OR EITHER PARTY HAD BEEN ADVISED IN ADVANCE
            OF THE POSSIBILITY OF SUCH LOSS OR DAMAGE. IN NO EVENT SHALL THE
            LIABILITY OF EITHER PARTY FOR ALL CLAIMS OF ANY KIND ARISING FROM
            THIS AGREEMENT EXCEED THE TOTAL FEES ACTUALLY PAID AND DUE TO
            MERITFIRST UNDER THIS AGREEMENT. MULTIPLE CLAIMS SHALL NOT EXPAND
            THIS LIMITATION. THE LIMITATION OF DAMAGES CONTAINED HEREIN REFLECT
            THE ALLOCATION OF RISK AMONG THE PARTIES, AND SHALL SURVIVE AND
            APPLY EVEN IF ANY LIMITED REMEDY SPECIFIED IN THIS AGREEMENT IS
            FOUND TO HAVE FAILED OF ITS ESSENTIAL PURPOSE. The limitations
            contained in Section 7.2 shall not apply to limit a party's
            liability for claims resulting from such party's breach of Section 5
            or for claims arising from Sections 8.2 or 8.3.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>8.</strong>
          </p>
          <p className="flex-1">
            <strong>Relationship and Assumption of Risk.</strong>
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">8.1</p>
          <p className="flex-1">
            <span className="underline">Independent Contractor.</span>{" "}
            MeritFirst is an independent contractor. Nothing herein shall be
            deemed or construed to create a relationship of principal and agent
            or of employer and employee between the parties, or to create a
            partnership, joint venture, or similar relationship between the
            parties.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">8.2</p>
          <p className="flex-1">
            <span className="underline">Assumption of Risk.</span> Customer has
            reviewed MeritFirst's interview techniques, processes, and
            curriculum and its methods for scoring interview results to confirm
            that they comply with Customer's own policies and procedures and
            applicable law. Customer is solely responsible for determining
            whether and how to use the Exam Results in its hiring decisions and
            for its hiring decisions. Customer assumes all risks arising from
            Customer's hiring and employment activities and decisions. Customer
            releases and agrees to defend, indemnify, and hold MeritFirst
            harmless for any judgment or liability imposed against MeritFirst
            from any claims asserted against MeritFirst by a third party by
            reason of Customer's use of the Exam Results other than as
            instructed by MeritFirst; provided that Customer shall have no
            obligation to defend, indemnify, and hold MeritFirst harmless with
            respect to that portion of any loss arising from MeritFirst's or its
            agents' violation of applicable laws, infringement, negligence or
            willful misconduct.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">8.3</p>
          <p className="flex-1">
            <span className="underline">MeritFirst Indemnity.</span> MeritFirst
            agrees to indemnify, defend and hold harmless Customer from and
            against any judgment or liability imposed against Customer from any
            claims asserted against Customer by a third party by reason of (a)
            MeritFirst's gross negligence or willful misconduct or (b) an
            allegation that the Licensed Materials infringe any valid patent or
            copyright; provided that MeritFirst shall have no obligation to
            indemnify Customer with respect to that portion of any loss covered
            by the indemnity in Section 8.2, from Customer's violation of
            applicable laws, negligence or willful misconduct, or arising from
            MeritFirst's compliance with Customer's written instructions and
            provided further that the obligations of MeritFirst with respect to
            claims set forth in subsection (b) above do not apply with respect
            to the Services, Licensed Materials, or any information, technology,
            materials or data (or any portions or components of the foregoing)
            to the extent (i) not created or provided by MeritFirst (including
            without limitation any Customer Data), (ii) made in whole or in part
            in accordance to Customer specifications, (iii) modified after
            delivery by MeritFirst, (iv) combined with other products, processes
            or materials not provided by MeritFirst (where the alleged Losses
            arise from or relate to such combination), (v) where Customer
            continues allegedly infringing activity after being notified thereof
            or after being informed of modifications that would have avoided the
            alleged infringement, or (vi) Customer's use of the Services is not
            strictly in accordance herewith.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">8.4</p>
          <p className="flex-1">
            <span className="underline">Procedures.</span> Each party's
            indemnification obligations hereunder shall be conditioned upon the
            indemnitee providing the indemnitor with: (a) prompt written notice
            of any claim (provided that a failure to provide such notice shall
            only relieve the indemnitor of its indemnity obligations if the
            indemnitor is materially prejudiced by such failure); (b) the option
            to assume sole control over the defense and settlement of any claim
            (provided that the indemnitee may participate in such defense and
            settlement at its own expense); and (b) reasonable information and
            assistance in connection with such defense and settlement (at the
            indemnitor's expense).
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>9.</strong>
          </p>
          <p className="flex-1">
            <strong>Attorneys' Fees; Governing Law.</strong> The prevailing
            party in any dispute arising under this Agreement shall be entitled
            to an award of its reasonable attorneys' fees, costs and expenses,
            incurred in connection with such dispute. This Agreement shall be
            construed and interpreted in accordance with the internal laws of
            the State of Delaware, without regard to its conflict of laws
            principles. Each Party consents to the exclusive jurisdiction and
            venue of the state and federal courts located in Delaware for any
            equitable claim or other action related to or arising from this
            Agreement or any terms and conditions contained herein.
          </p>
        </div>
        <div className="flex items-start">
          <p className="!w-[30px]">
            <strong>10.</strong>
          </p>
          <p className="flex-1">
            <strong>Miscellaneous.</strong> This Agreement represents the entire
            agreement between Customer and MeritFirst with respect to the
            subject matter hereof, and supersedes all prior or contemporaneous
            communications and proposals (whether oral, written or electronic)
            between Customer and MeritFirst with respect thereto. All notices
            under this Agreement shall be in writing and shall be deemed to have
            been duly given when received, if personally delivered or sent by
            certified or registered mail, return receipt requested; when receipt
            is electronically confirmed, if transmitted by facsimile or e-mail;
            or the day after it is sent, if sent for next day delivery by
            recognized overnight delivery service. Notices must be sent to the
            contacts for each party set forth on the Order. Either party may
            update its address set forth above by giving notice in accordance
            with this section. Except as otherwise provided herein, this
            Agreement may be amended only by a writing executed by both parties.
            Except for payment obligations, neither party shall be liable for
            any failure to perform its obligations hereunder where such failure
            results from any cause beyond such party's reasonable control,
            including, without limitation, the elements; fire; flood; severe
            weather; earthquake; vandalism; accidents; sabotage; power failure;
            denial of service attacks or similar attacks; Internet failure; acts
            of God and the public enemy; acts of war; acts of terrorism; riots;
            civil or public disturbances; strikes lock-outs or labor
            disruptions; any laws, orders, rules, regulations, acts or
            restraints of any government or governmental body or authority,
            civil or military, including the orders and judgments of courts.
            Neither party may assign any of its rights or obligations hereunder
            without the other party's consent; provided that (a) either party
            may assign all of its rights and obligations hereunder without such
            consent to a successor-in-interest in connection with a sale of
            substantially all of such party's business relating to this
            Agreement, and (b) MeritFirst may utilize subcontractors in the
            performance of its obligations hereunder. No agency, partnership,
            joint venture, or employment relationship is created as a result of
            this Agreement and neither party has any authority of any kind to
            bind the other in any respect. Each party acknowledges and agrees
            that due to the unique nature of the other party's Confidential
            Information, there can be no adequate remedy at law for any breach
            of such party's obligations under Section 5, which breach may result
            in irreparable harm to such other party, and therefore, that upon
            any such breach or any threat thereof, such other party shall be
            entitled to appropriate equitable relief, without the requirement of
            posting a bond, in addition to whatever remedies it might have at
            law. In any action or proceeding to enforce rights under this
            Agreement, the prevailing party shall be entitled to recover costs
            and attorneys' fees. If any provision of this Agreement is held to
            be unenforceable for any reason, such provision shall be reformed
            only to the extent necessary to make it enforceable. The failure of
            either party to act with respect to a breach of this Agreement by
            the other party shall not constitute a waiver and shall not limit
            such party's rights with respect to such breach or any subsequent
            breaches. Customer hereby grants MeritFirst the right to use
            Customer's name and logo (i) on MeritFirst's website and promotional
            features to identify Customer as a customer of MeritFirst, (ii)
            include in MeritFirst's promotional materials utilization statistics
            relating to Company's use of the Licensed Materials, and (iii) as
            necessary in the course of providing the Services to identify
            MeritFirst and Customer's relationship to Candidates.
          </p>
        </div>
      </div>
    </div>
  )
}
